import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ClipboardList,
  FileText,
  Film,
  ScrollText,
  type LucideIcon,
} from "lucide-react";
import { PageHeader } from "./components/PageHeader";
import { NotesPage } from "./NotesPage";
import { ClipsPage } from "./ClipsPage";
import { MatchSummariesPage } from "./MatchSummariesPage";
import type { GoalComposerSource } from "./components/GoalComposerModal";

export type FeedbackLibrarySection =
  | "notes"
  | "observations"
  | "clips"
  | "recaps";

const SECTIONS: ReadonlyArray<{
  id: FeedbackLibrarySection;
  label: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    id: "notes",
    label: "Notes",
    description: "Player-facing teaching points and coach notes.",
    icon: FileText,
  },
  {
    id: "observations",
    label: "Observations",
    description: "Tactical-board and non-video observations.",
    icon: ClipboardList,
  },
  {
    id: "clips",
    label: "Clips",
    description: "Saved video evidence windows.",
    icon: Film,
  },
  {
    id: "recaps",
    label: "Match Recaps",
    description: "Team-level match summaries and linked evidence.",
    icon: ScrollText,
  },
];

export type FeedbackLibraryPageProps = {
  onCreatePlayerGoal?: (
    playerId: string,
    source: GoalComposerSource,
  ) => void;
};

export function FeedbackLibraryPage({
  onCreatePlayerGoal,
}: FeedbackLibraryPageProps) {
  const navigate = useNavigate({ from: "/coach" });
  const search = useSearch({ from: "/coach" });
  const section = resolveLibrarySection(search.tab, search.library);

  const selectSection = (next: FeedbackLibrarySection) => {
    void navigate({
      to: "/coach",
      search: { tab: "library", library: next },
    });
  };

  return (
    <div className="flex flex-col gap-5" data-testid="feedback-library-page">
      <PageHeader
        title="Feedback Library"
        description="One place to find coach-created feedback objects: notes, observations, clips, and match recaps."
      />

      <section
        className="-mx-1 overflow-x-auto px-1"
        aria-label="Feedback library sections"
        data-testid="feedback-library-sections"
      >
        <div className="inline-flex min-w-full gap-1 rounded border border-rule bg-surface p-1 shadow-sm">
          {SECTIONS.map((item) => {
            const selected = item.id === section;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => selectSection(item.id)}
                aria-pressed={selected}
                title={item.description}
                data-testid={`feedback-library-section-${item.id}`}
                className={[
                  "inline-flex h-9 flex-1 items-center justify-center gap-2 rounded px-3 text-sm font-display font-semibold uppercase tracking-wider transition-colors",
                  selected
                    ? "bg-accent text-accent-ink"
                    : "text-ink-muted hover:bg-field hover:text-ink",
                ].join(" ")}
              >
                <Icon size={16} strokeWidth={2} aria-hidden="true" />
                <span className="whitespace-nowrap">{item.label}</span>
              </button>
            );
          })}
        </div>
      </section>

      <div data-testid={`feedback-library-active-${section}`}>
        {section === "notes" ? (
          <NotesPage mode="notes" onCreatePlayerGoal={onCreatePlayerGoal} />
        ) : section === "observations" ? (
          <NotesPage
            mode="observations"
            onCreatePlayerGoal={onCreatePlayerGoal}
          />
        ) : section === "clips" ? (
          <ClipsPage onCreatePlayerGoal={onCreatePlayerGoal} />
        ) : (
          <MatchSummariesPage />
        )}
      </div>
    </div>
  );
}

function resolveLibrarySection(
  tab: string | undefined,
  library: FeedbackLibrarySection | undefined,
): FeedbackLibrarySection {
  if (tab === "notes") return "notes";
  if (tab === "clips") return "clips";
  if (tab === "summaries") return "recaps";
  return library ?? "notes";
}
